// ============================================================================
// MÓDULO UTILITÁRIO: QUESTION LOADER (Q-ENGINE V15.0 - DEEP FILTER)
// ARQUIVO: src/utils/QuestionLoader.js
// OBJETIVO: Motor de busca de alta performance com suporte a filtros aninhados.
// REGRAS: T.C.R.E.I. Compliance / Anti-Crash / Firestore Path Strict.
// ============================================================================

import { collection, getDocs, query, limit, orderBy } from 'firebase/firestore';
import { db, auth } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import { FS } from '../shared/constants/firestorePaths';

const appId = FIREBASE_CONFIG.projectId || 'portalrda-f312d';

/**
 * Adapter: converte o Schema Aninhado Padrão Ouro (30 campos) para o formato flat
 * legado consumido pelo SimuladoEngine e pelos filtros existentes.
 * Preserva os dados brutos em __raw para acesso aos campos enriquecidos.
 */
function adaptNestedToFlat(doc) {
    // Fallback para documentos antigos ainda no formato flat
    if (!doc.metadata && !doc.taxonomy && !doc.content && !doc.compliance) {
        return doc;
    }

    const meta = doc.metadata || {};
    const tax = doc.taxonomy || {};
    const cnt = doc.content || {};
    const comp = doc.compliance || {};
    const norma = comp.norma || {};

    // Converte alternativas do objeto {a,b,c,d} para array [{id,text}]
    const alts = cnt.alternativas || {};
    const alternatives = [
        { id: 'A', text: alts.a || '' },
        { id: 'B', text: alts.b || '' },
        { id: 'C', text: alts.c || '' },
        { id: 'D', text: alts.d || '' },
    ];

    // Converte alt_correta ('A','B','C','D') para índice numérico (0,1,2,3)
    const corretaLetter = (cnt.alt_correta || 'A').toUpperCase().trim();
    const corretaIndex = ['A', 'B', 'C', 'D'].indexOf(corretaLetter);

    return {
        id: doc.id,
        // Campos flat (backward compatibility)
        materia: tax.materia || 'GERAL',
        banca: meta.banca || 'FGV',
        year: meta.ano || new Date().getFullYear(),
        exam: meta.exame || 'N/A',
        number: meta.numero || 0,
        subject_macro: tax.assunto_macro || '',
        subject_micro: tax.assunto_especifico || '',
        difficulty: tax.dificuldade || 'MEDIUM',
        enunciation: cnt.enunciado || '',
        alternatives,
        correta: corretaIndex >= 0 ? corretaIndex : 0,
        justification: cnt.justificativa || '',
        status_juridico: comp.status_questao || 'VALIDA',
        normative_status: comp.status_normativo || 'VIGENTE',
        legal_meta: {
            norm_type: norma.tipo || 'LEI_FED',
            article: norma.artigo || '',
            link: norma.link_oficial || '',
            norm_name: norma.numero || '',
            inciso: norma.inciso || '',
        },
        video_resolucao_id: cnt.link_video || '',
        observacoes: cnt.observacoes || '',
        data_prova: meta.data_prova || '',
        gabarito_preliminar: comp.gabarito_preliminar || '',
        gabarito_definitivo: comp.gabarito_definitivo || '',
        data_ultima_validacao: comp.data_ultima_validacao || '',
        jurisprudencia_relacionada: comp.jurisprudencia_relacionada || '',
        // Dados brutos para acesso aos 30 campos enriquecidos
        __raw: doc,
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
    };
}

export const QuestionLoader = {
    
    /**
     * Sincroniza a base de dados do Firestore com o sistema local.
     * Implementa o protocolo de Resiliência Total (Art. 29 MGO).
     * O SDK v10 gerencia o IndexedDB automaticamente; cache manual removido.
     */
    async loadDatabase() {
        try {
            console.log("📡 [Q-ENGINE] Iniciando Sincronização Titanium...");
            
            // REGRA 1 - Caminho Estrito (Strict Paths)
            const colRef = collection(db, ...FS.questions(appId));
            
            // REGRA 2 - Consulta Simples (Evita erros de índice composto em tempo real)
            const q = query(colRef, orderBy('createdAt', 'desc'), limit(1000));
            const snap = await getDocs(q);
            
            if (snap.empty) {
                console.warn("⚠️ [Q-ENGINE] Firestore vazio ou sem permissão. Ativando Modo de Emergência.");
                throw new Error("Empty_DB");
            }
            
            const data = snap.docs.map(d => adaptNestedToFlat({ 
                id: d.id, 
                ...d.data() 
            }));

            console.log(`✅ [Q-ENGINE] ${data.length} questões ricas carregadas com sucesso.`);
            return data;

        } catch (error) { 
            console.error("❌ [Q-ENGINE] Erro Crítico no Loader:", error.message);
            return this.getEmergencyMocks();
        }
    },

    /**
     * Motor de Filtragem Transversal (O Diferencial Padrão Ouro).
     * Filtra por Banca, Ano, Matéria e metadados legislativos (Artigo/Súmula).
     */
    async getQuestions(filters = {}) {
        const allQuestions = await this.loadDatabase();
        
        let filtered = allQuestions.filter(q => {
            // 1. Filtro de Disciplina
            if (filters.discipline && filters.discipline !== 'ALL' && q.materia !== filters.discipline) return false;
            
            // 2. Filtro de Origem (Banca e Ano)
            if (filters.bank && filters.bank !== 'ALL' && q.banca !== filters.bank) return false;
            if (filters.year && filters.year !== 'ALL' && String(q.year) !== String(filters.year)) return false;
            
            // 3. Filtro de Dificuldade (Gamificação)
            if (filters.difficulty && filters.difficulty !== 'ALL' && q.difficulty !== filters.difficulty) return false;

            // 4. FILTRO DE ENGENHARIA REVERSA (NOVO V15.0)
            // Filtra por Tipo de Norma (Súmula, Lei, CF) - Critical Path
            if (filters.normType && filters.normType !== 'ALL') {
                const qNorm = q.legal_meta?.norm_type || 'OUTROS';
                if (qNorm !== filters.normType) return false;
            }

            // 5. Filtro de Status Jurídico (Validade)
            if (filters.status && filters.status !== 'ALL') {
                const qStatus = q.status_juridico || q.status || 'VALIDA'; // Fallback para schemas antigos
                if (qStatus !== filters.status) return false;
            }

            // 6. BUSCA SEMÂNTICA TRANSVERSAL (The Law Search)
            // Varre enunciado, justificativa e o link da lei simultaneamente
            if (filters.term && filters.term.length > 2) {
                const term = filters.term.toLowerCase();
                const searchableText = [
                    q.enunciation,
                    q.justification,
                    q.subject_macro,
                    q.subject_micro,
                    q.legal_meta?.norm_name,
                    q.legal_meta?.article,
                    `Art. ${q.legal_meta?.article}`,
                    `Artigo ${q.legal_meta?.article}`
                ].join(' ').toLowerCase();
                
                if (!searchableText.includes(term)) return false;
            }

            return true;
        });

        // Protocolo de Aleatoriedade (Shuffle)
        if (filters.shuffle !== false) { 
            for (let i = filtered.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
            }
        }

        // Limite de amostragem para evitar estouro de memória no cliente
        const finalLimit = filters.limit || 50;
        return filtered.slice(0, finalLimit);
    },

    /**
     * Gera estatísticas em tempo real para o Dashboard do Aluno.
     */
    async getStats() {
        const data = await this.loadDatabase();
        const stats = { 
            total: data.length, 
            bySubject: {},
            lastSync: new Date().toISOString()
        };

        data.forEach(q => {
            const s = q.materia || 'OUTROS';
            stats.bySubject[s] = (stats.bySubject[s] || 0) + 1;
        });

        return stats;
    },

    /**
     * Mocks de Alta Fidelidade (Somente para contingência).
     */
    getEmergencyMocks() {
        return [{ 
            id: 'emergency_mock', 
            materia: 'ETICA',
            banca: 'FGV',
            year: 2026,
            enunciation: 'SISTEMA EM MODO DE SEGURANÇA: O banco de dados remoto não retornou os dados. Por favor, verifique sua conexão ou use o Ingestor de Dados no Painel Admin.',
            alternatives: [
                { id: 'A', text: 'Entendido. Vou verificar o Admin.' },
                { id: 'B', text: 'Tentar reconectar agora.' }
            ],
            correta: 0,
            justification: 'Este é um estado de erro controlado para evitar a tela branca.',
            legal_meta: { norm_name: 'Constituição RDA', article: '29', norm_type: 'CF' }
        }];
    }
};